import { useCallback, useEffect, useRef, useState } from 'react';
import { RoomEvent } from 'livekit-client';
import { useRoomContext } from '@livekit/components-react';
import { FILTER_IDS } from './filters';

const TRANSIENT_MS = 2000;

export function useFilterState({ socket, identity, isHost }) {
  const room = useRoomContext();
  const [activeFilterId, setActiveFilterId] = useState(FILTER_IDS.NEUTRAL);
  const standingRef = useRef(FILTER_IDS.NEUTRAL);
  const transientTimerRef = useRef(null);

  const apply = useCallback(
    ({ standings, transient }) => {
      if (!identity) return;
      const standingFilter = standings?.[identity] || FILTER_IDS.NEUTRAL;
      standingRef.current = standingFilter;

      const transientFilter = transient?.[identity];
      if (transientFilter) {
        setActiveFilterId(transientFilter);
        if (transientTimerRef.current) clearTimeout(transientTimerRef.current);
        transientTimerRef.current = setTimeout(() => {
          setActiveFilterId(standingRef.current);
          transientTimerRef.current = null;
        }, TRANSIENT_MS);
      } else {
        if (transientTimerRef.current) {
          clearTimeout(transientTimerRef.current);
          transientTimerRef.current = null;
        }
        setActiveFilterId(standingFilter);
      }
    },
    [identity]
  );

  // Host receives directly from the server.
  useEffect(() => {
    if (!isHost || !socket) return undefined;
    const handler = (data) => apply(data);
    socket.on('filter_states', handler);
    return () => socket.off('filter_states', handler);
  }, [isHost, socket, apply]);

  // Guests (and broadcast observers) receive via the LiveKit data channel.
  useEffect(() => {
    if (!room) return undefined;
    const handler = (payload) => {
      try {
        const decoded = JSON.parse(new TextDecoder().decode(payload));
        if (decoded?.type === 'filterState') {
          apply({ standings: decoded.standings, transient: decoded.transient });
        }
      } catch (_) {
        /* malformed payload — ignore */
      }
    };
    room.on(RoomEvent.DataReceived, handler);
    return () => room.off(RoomEvent.DataReceived, handler);
  }, [room, apply]);

  useEffect(
    () => () => {
      if (transientTimerRef.current) clearTimeout(transientTimerRef.current);
    },
    []
  );

  return activeFilterId;
}
